import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";

export interface AttachmentMetadata {
  filename?: string;
  signed_id: string;
  url: string;
}

interface PageWithAttachments extends BaseBlock {
  inline_attachments?: AttachmentMetadata[];
}

type AttachmentMap = Map<string, AttachmentMetadata>;
type ReadonlyAttachmentMap = ReadonlyMap<string, AttachmentMetadata>;

interface AttachmentContextType {
  addLocalAttachment: (attachment: AttachmentMetadata) => void;
  attachments: ReadonlyAttachmentMap;
  getAllAttachments: () => ReadonlyAttachmentMap;
}

const AttachmentContext = createContext<AttachmentContextType>({
  attachments: new Map(),
  getAllAttachments: () => new Map(),
  addLocalAttachment: () => undefined,
});

AttachmentContext.displayName = "AttachmentContext";

interface AttachmentProviderProps {
  attachments?: AttachmentMetadata[];
  children: ReactNode;
  pages?: Record<string, PageWithAttachments>;
}

export const AttachmentProvider = ({
  children,
  attachments = [],
  pages,
}: AttachmentProviderProps) => {
  const [localAttachments, setLocalAttachments] = useState<AttachmentMap>(
    new Map(),
  );

  const contextValue = useMemo(() => {
    const attachmentMapFromArray = (
      list: AttachmentMetadata[],
    ): AttachmentMap =>
      new Map(list.map(attachment => [attachment.signed_id, attachment]));

    const getAllAttachments = (): AttachmentMap => {
      const allAttachments = attachmentMapFromArray(attachments);

      if (pages) {
        const inlineAttachments = Object.values(pages).flatMap(
          page => page.inline_attachments ?? [],
        );
        inlineAttachments.forEach(attachment => {
          allAttachments.set(attachment.signed_id, attachment);
        });
      }

      localAttachments.forEach((attachment, signed_id) => {
        if (!allAttachments.has(signed_id)) {
          allAttachments.set(signed_id, attachment);
        }
      });

      return allAttachments;
    };

    const addLocalAttachment = (attachment: AttachmentMetadata) => {
      setLocalAttachments(prev => {
        const next = new Map(prev);
        next.set(attachment.signed_id, attachment);
        return next;
      });
    };

    const mergedAttachments = getAllAttachments();

    return {
      attachments: mergedAttachments,
      getAllAttachments,
      addLocalAttachment,
    };
  }, [attachments, pages, localAttachments]);

  return (
    <AttachmentContext.Provider value={contextValue}>
      {children}
    </AttachmentContext.Provider>
  );
};

export const useAttachments = (): AttachmentContextType => {
  const context = useContext(AttachmentContext);
  if (!context) {
    throw new Error("useAttachments must be used within an AttachmentProvider");
  }
  return context;
};

export const useAttachmentMetadata = (
  signed_id: string,
): AttachmentMetadata | null => {
  const { getAllAttachments } = useAttachments();
  return getAllAttachments().get(signed_id) ?? null;
};
